import { createError } from '@adv-frontend/error';
import type { Flow } from '../../contract/flow';
import type { FlowSubscription } from '../../contract/subscription';
import { isBypassSsr } from '../../utils/bypassSsrCheck';
import { isServer } from '../../utils/isServer';
import { getFlowRevision } from '../../utils/flowRevision';
import type { FlowComputationBase } from './computation';

/** Ошибка при вызове подписчиков потока */
export const FlowListenerError = createError('FlowListenerError');

/**
 * Предел повторов пересчета, когда геттер меняет собственные источники.
 * Нужен как страховка от геттера, который меняет источник на каждом запуске.
 */
const MAX_RESTABILIZE_ATTEMPTS = 5;

/**
 * Внутренний объект подписки, расширяющий публичный интерфейс FlowSubscription.
 *
 * Этот интерфейс используется внутри реализаций вычисляемых потоков для хранения как публичного
 * метода отписки, так и приватной функции слушателя для каждой подписки.
 *
 * @internal
 */
export interface Subscription extends FlowSubscription {
    /**
     * Функция слушателя, которая будет вызвана при изменении значения потока.
     * @internal
     */
    listener: () => void;
}

/**
 * Абстрактный базовый класс для вычисляемых потоков, который предоставляет управление подписками
 * и функциональность кеширования значений.
 *
 * @typeParam T - Тип значения, которое производит этот поток
 * @typeParam FlowComputation - Тип вычисления, используемого этим потоком
 *
 * @example
 * ```typescript
 * class MyComputedFlow extends ComputedFlowBase<number, MyComputation> {
 *   protected compute(): MyComputation {
 *     return new MyComputation(() => this.calculateValue());
 *   }
 *
 *   private calculateValue(): number {
 *     // Детали реализации
 *     return 42;
 *   }
 * }
 * ```
 */
export abstract class ComputedFlowBase<T, FlowComputation extends FlowComputationBase<T, unknown>> implements Flow<T> {
    /**
     * Ссылка на результат последнего вычисления, используемая для кеширования значений.
     * Это вычисление не подписано на источники и существует только для кеширования значений.
     */
    protected cachedComputation: FlowComputation | null = null;

    /**
     * Ссылка на вычисление, которое активно подписано на изменения источников.
     * Она поддерживается, пока есть активные подписки на этот поток,
     * и очищается при удалении последней подписки.
     */
    protected activeComputation: FlowComputation | null = null;

    /**
     * Указывает, есть ли активные подписки на этот вычисляемый поток.
     */
    private hasListeners = false;

    /**
     * Указывает, нужно ли пересчитать значение потока.
     * Устанавливается в true при изменении исходных потоков, сбрасывается в false после пересчета.
     */
    private isDirty = true;

    /**
     * Глубина вложенности вычислений этого потока, идущих прямо сейчас.
     * Счетчик, а не флаг: геттер может дернуть getSnapshot() того же потока повторно.
     */
    private computeDepth = 0;

    /**
     * Набор функций слушателей, которые вызываются при изменении значения.
     */
    private subscriptions: ReadonlySet<Subscription> = new Set<Subscription>();

    /**
     * Идет ли прямо сейчас вычисление этого потока.
     *
     * Пока вычисление не установлено в кеш, уведомлять о его состоянии нельзя: подписчик прочитал
     * бы прежнее значение. О переходе такого вычисления сообщает {@link onComputationInstalled}.
     */
    protected get isComputing(): boolean {
        return this.computeDepth > 0;
    }

    /**
     * Подписывается на изменения в потоке.
     *
     * Функция слушателя вызывается синхронно при изменении материализованного состояния потока:
     * на любом чтении, установившем новое состояние, и, у асинхронных подклассов, на завершении
     * вычисления. Такие уведомления доставляются всегда, в том числе тому, кто поток не читал:
     * чтение одного потребителя меняет состояние для всех.
     *
     * Отдельный повод позвать слушателя — инвалидация: источники изменились, поэтому кеш мог
     * устареть, но состояние потока при этом остается прежним, пока его никто не перечитал.
     * Инвалидации схлопываются — до следующего {@link getSnapshot} придет не больше одного
     * уведомления.
     *
     * Слушатель не получает параметров и должен использовать {@link getSnapshot} или
     * {@link getCachedSnapshot} для доступа к текущему значению.
     *
     * Подписка активирует поток, но не запускает его собственный геттер: `compute()` не вызывается
     * ни в одной ветке.
     *
     * - Поток еще ни разу не вычислялся: подписка ничего не вычисляет и только помечает поток
     *   активным. Список источников неизвестен, поэтому трекинг начинается с первого
     *   {@link getSnapshot}. Следствие: `subscribe()` без единого {@link getSnapshot} на никогда
     *   не вычислявшемся потоке уведомлений не порождает.
     * - Поток уже вычислялся ранее: подписка сразу восстанавливает подписку на известный набор
     *   источников и, если они изменились, синхронно уведомляет нового слушателя. Проверка
     *   изменений читает источники через `getSnapshot()`, поэтому источник, который сам является
     *   вычисляемым потоком, при этом может пересчитаться. Полностью ленивое повторное
     *   подписывание — DIRECT-321934.
     *
     * Когда удаляется последняя подписка, поток перестанет отслеживать источники.
     *
     * @param listener - Функция, которая будет вызвана при изменении значения
     * @returns Объект подписки, который может быть использован для отписки от изменений
     *
     * @example
     * ```typescript
     * const sourceFlow = createFlow(0);
     * const doubledFlow = computedFlow((ctx) => ctx.watch(sourceFlow) * 2);
     *
     * const subscription = doubledFlow.subscribe(() => {
     *   console.log('Новое значение:', doubledFlow.getSnapshot());
     * });
     *
     * // Первое чтение поднимает трекинг источников
     * doubledFlow.getSnapshot(); // 0
     *
     * sourceFlow.emit(1); // Вызывает слушателя
     * subscription.unsubscribe(); // Прекращает прослушивание изменений
     * ```
     */
    public subscribe(listener: () => void): FlowSubscription {
        if (isServer() && !isBypassSsr()) {
            throw new Error('Server-side flow subscriptions are not supported');
        }
        const subscription: Subscription = {
            listener,
            unsubscribe: () => {
                const subscriptions = new Set(this.subscriptions);
                subscriptions.delete(subscription);
                this.subscriptions = subscriptions;

                // Прекращаем прослушивание источников, когда удаляется последний подписчик.
                // Флаг hasListeners сбрасывается независимо от наличия activeComputation:
                // при ленивой подписке поток может быть активен, но еще ни разу не вычислен,
                // и тогда подписки на источники (activeComputation) еще нет.
                if (this.subscriptions.size === 0) {
                    this.hasListeners = false;

                    if (this.activeComputation) {
                        this.activeComputation.dispose();
                        this.activeComputation = null;
                    }
                }
            },
        };

        // Флаг: нужно ли уведомить нового подписчика о том, что данные уже устарели.
        // Выставляется до добавления подписки в список, но notify() вызывается после,
        // чтобы новый слушатель гарантированно оказался в this.subscriptions к моменту вызова.
        let shouldNotify = false;

        if (!this.hasListeners) {
            this.hasListeners = true;

            if (this.cachedComputation) {
                // Восстанавливаем подписки на закешированные источники безусловно, чтобы даже при
                // ленивом пересчёте (или его пропуске через ctx.skip()) поток гарантированно
                // остался подписан на источники.
                this.subscribeToSources(this.cachedComputation);

                // Новых подписчиков ещё никто не уведомлял, поэтому прошлое значение isDirty здесь
                // неактуально — пересчитываем его по фактическому состоянию источников. Иначе
                // возможен сценарий: пока не было подписчиков, источник изменился и вернулся к
                // закешированному значению — sourcesHasBeenChanged() вернёт false, а исходно
                // выставленный isDirty=true останется, и ближайший onSourcesChanged() пропустит
                // notify() (см. gate на isDirty), из-за чего подписчик пропустит все дальнейшие
                // инвалидации, пока кто-нибудь не вызовет getSnapshot().
                //
                // isFinalized() отсекает ещё выполняющееся асинхронное вычисление: его список
                // источников неполон, а о завершении оно уведомит само.
                shouldNotify = this.cachedComputation.isFinalized() && this.cachedComputation.sourcesHasBeenChanged();
                this.isDirty = shouldNotify;
            }

            // Если предыдущих вычислений не было, подписка ничего не вычисляет: список источников
            // еще неизвестен, а узнать его можно только прогоном геттера. Трекинг источников
            // поднимется на первом getSnapshot(): compute() -> onComputationFinished() ->
            // subscribeToSources() (hasListeners уже true). До этого момента поток активен,
            // но не подписан ни на что и уведомлений не порождает.
        }

        const subscriptions = new Set(this.subscriptions);
        subscriptions.add(subscription);
        this.subscriptions = subscriptions;

        if (shouldNotify) {
            this.notify();
        }

        return subscription;
    }

    /**
     * Возвращает текущее значение потока.
     *
     * Этот метод предоставляет синхронный доступ к текущему значению без
     * подписки на изменения. Его безопасно вызывать в любое время, и он всегда
     * вернет самое последнее значение.
     *
     * @returns Текущее значение
     *
     * @example
     * ```typescript
     * const flow = createFlow("initial");
     *
     * console.log(flow.getSnapshot()); // "initial"
     *
     * flow.emit("updated");
     * console.log(flow.getSnapshot()); // "updated"
     * ```
     */
    public getSnapshot(): T {
        if (!this.cachedComputation || this.shouldRecompute()) {
            // Прежнее вычисление запоминаем до расчета: revertComputation() в асинхронном
            // подклассе успевает переписать cachedComputation прямо во время вычисления.
            const previous = this.cachedComputation;

            this.computeDepth += 1;
            let computation: FlowComputation;
            try {
                computation = this.computeStable();
            } finally {
                this.computeDepth -= 1;
            }

            // Порядок важен: к моменту хука результат уже в кеше и гейт открыт, поэтому
            // слушатель, который читает поток прямо из уведомления, получит свежее значение
            // и не запустит повторное вычисление.
            this.cachedComputation = computation;
            this.isDirty = false;
            // После хука isDirty не трогаем: слушатель мог изменить источник, и эта инвалидация
            // обязана дожить до следующего чтения
            this.onComputationInstalled(previous, computation);
        } else {
            this.isDirty = false;
        }

        return this.cachedComputation.getValue();
    }

    /**
     * Выполняет вычисление и повторяет его, если источники изменились прямо во время расчета.
     *
     * Такое бывает, когда геттер сам пишет в поток, который перед этим прочитал, или когда
     * изменение источника прилетает каскадом из чужого emit. Флаг isDirty в этот момент занят
     * инвалидацией, которая и запустила пересчет, поэтому новое изменение не проходит гейт в
     * onSourcesChanged() и было бы потеряно: значение осталось бы посчитанным по старым данным,
     * а подписчики не получили бы уведомления и никогда бы не перечитали поток.
     *
     * Повторяем только для завершенных вычислений: у незавершенного асинхронного расчета список
     * источников еще неполон, и перезапуск по нему был бы неверен — такое вычисление само
     * уведомит о завершении.
     *
     * TODO DIRECT-286616 Временная самопочинка. Когда изменение потоков во время вычисления
     * станет ошибкой, цикл рестабилизации можно будет заменить на исключение по сдвигу
     * flowRevision за время расчета.
     *
     * @returns Вычисление, согласованное с текущими значениями источников
     */
    private computeStable(): FlowComputation {
        const previous = this.cachedComputation;
        let revisionBefore = getFlowRevision();
        let computation = this.compute();

        for (let attempt = 0; attempt < MAX_RESTABILIZE_ATTEMPTS; attempt += 1) {
            // За время расчета не менялся ни один мутабельный поток — сверять источники не нужно.
            // Это основной путь, и он не стоит ни одного лишнего чтения источника.
            if (getFlowRevision() === revisionBefore) {
                break;
            }
            // Откат к прежнему вычислению (ctx.skip()) — пересчитывать нечего, каждая попытка
            // вернет тот же объект.
            if (computation === previous) {
                break;
            }
            // У незавершенного асинхронного расчета список источников еще неполон — сравнивать
            // его с текущими значениями нельзя, а о завершении он уведомит сам.
            if (!computation.isFinalized()) {
                break;
            }
            if (!computation.sourcesHasBeenChanged()) {
                break;
            }

            revisionBefore = getFlowRevision();
            const next = this.compute();
            if (next === computation) {
                break;
            }

            computation = next;

            // Повторная попытка откатилась к прежнему вычислению (ctx.skip()) — принимаем откат.
            if (next === previous) {
                break;
            }
        }

        return computation;
    }

    /**
     * Возвращает последнее закешированное значение потока без запуска вычисления.
     *
     * Если последнее материализованное вычисление завершилось ошибкой, метод выбрасывает
     * эту ошибку: это часть контракта, а не побочный эффект.
     *
     * @returns Закешированное значение, либо `undefined`, если поток еще ни разу
     *   не вычислялся.
     */
    public getCachedSnapshot(): T | undefined {
        return this.cachedComputation?.getValue();
    }

    /**
     * Определяет, нужно ли пересчитать кешированное вычисление.
     *
     * @returns true, если нужен пересчет, false в противном случае
     */
    private shouldRecompute(): boolean {
        // Случаи, требующие пересчета:
        // 1. Если мы в данный момент не отслеживаем источники, кешированное значение может быть устаревшим,
        //    поэтому нам нужно проверить текущие значения в источниках (флаг hasListeners)
        // 2. Если источники изменились с момента последнего запуска (флаг isDirty)
        return (
            (!this.hasListeners || this.isDirty) &&
            (!this.cachedComputation || this.cachedComputation.sourcesHasBeenChanged())
        );
    }

    /**
     * Абстрактный метод для вычисления значения потока.
     * Должен быть реализован подклассами для определения логики вычисления.
     *
     * @returns Новый экземпляр вычисления, содержащий вычисленное значение
     */
    protected abstract compute(): FlowComputation;

    /**
     * Вызывается после успешного вычисления для управления подписками на источники.
     * Подписывается на новый список источников и отписывается от предыдущих источников.
     *
     * @param computation - Вычисление, которое только что было завершено
     */
    protected onComputationFinished(computation: FlowComputation) {
        if (this.hasListeners) {
            this.subscribeToSources(computation);
        }
    }

    /**
     * Уведомляет о смене материализованного значения после установки вычисления в кеш.
     *
     * Материализация — состоявшийся переход, а не инвалидация, поэтому уведомление идет мимо
     * гейта isDirty. Читает один потребитель, а значение меняется для всех, поэтому знать о
     * переходе обязан и тот, кто поток не читал: иначе подписчик, живущий на
     * {@link getCachedSnapshot}, остался бы с прежним значением.
     *
     * Результаты сравниваем без побочных эффектов: откат через `ctx.skip()` приходит сюда с
     * `previous === current`, а пересчет, совпавший с прежним значением по `options.equals`,
     * переиспользует его — оба уведомления не порождают.
     *
     * @param previous - Вычисление, лежавшее в кеше до расчета, либо null
     * @param current - Вычисление, установленное в кеш
     */
    protected onComputationInstalled(previous: FlowComputation | null, current: FlowComputation): void {
        if (previous === current || previous?.hasSameOutcome(current)) {
            return;
        }

        this.notify();
    }

    /**
     * Подписывается на источники вычисления.
     *
     * @param computation - Вычисление, на источники которого нужно подписаться
     */
    private subscribeToSources(computation: FlowComputation) {
        if (this.activeComputation) {
            // Отписываемся от предыдущего списка источников
            this.activeComputation.dispose();
            this.activeComputation = null;
        }

        computation.subscribeToSources(() => {
            // Проверяем, что уведомление приходит от текущего списка источников
            if (this.activeComputation === computation) {
                this.onSourcesChanged();
            }
        });

        this.activeComputation = computation;
    }

    /**
     * Вызывается, когда исходные потоки изменяют свои значения.
     *
     * Гейт по isDirty схлопывает повторные инвалидации: до следующего {@link getSnapshot}
     * подписчики получат не больше одного уведомления. Материализация нового состояния при
     * чтении и завершение асинхронного вычисления идут мимо этого гейта — это состоявшиеся
     * изменения состояния, а не инвалидация.
     */
    protected onSourcesChanged() {
        if (!this.isDirty) {
            this.isDirty = true;
            this.notify();
        }
    }

    /**
     * Уведомляет всех подписчиков об изменениях в значении потока.
     */
    protected notify(): void {
        for (const subscription of this.subscriptions) {
            try {
                subscription.listener();
            } catch (error) {
                console.error(new FlowListenerError('Failed to call computed flow listener', { cause: error }));
            }
        }
    }
}
